// A curve is a NAMED point series, one row per point, sharing the curve's
// name — real SWMM only writes the Type token on the FIRST row. That first
// row can also legally carry ONLY `Name Type` with no X-Y pair at all,
// every point arriving on later continuation rows — whether a row IS the
// first one must be tracked by whether this curve's name has been seen
// yet, never guessed from the row's own token count, which would mistake
// that legal shape for a continuation row missing its Y value (turning it
// into a phantom NaN point and losing the curve's type entirely).

import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";

export interface CurvePoint {
  x: number;
  y: number;
}

export interface Curve {
  name: string;
  type: string;
  points: CurvePoint[];
}

/** Pump1-5 are the five standard pump-curve shapes, not unrelated kinds — PUMP5 a variable-speed variant of PUMP3. */
export const CURVE_TYPES = [
  "STORAGE",
  "PUMP1",
  "PUMP2",
  "PUMP3",
  "PUMP4",
  "PUMP5",
  "RATING",
  "CONTROL",
  "DIVERSION",
  "SHAPE",
  "TIDAL",
] as const;

export function readCurves(raw: ReadonlyMap<string, string[][]>, warnings?: Set<string>): Curve[] {
  const curves: Curve[] = [];
  const byName = new Map<string, Curve>();
  for (const row of raw.get("CURVES") ?? []) {
    const name = row[0];
    if (!name) continue;
    // Whether THIS is the curve's first row is tracked by whether its name
    // has been seen yet — not by this row's own token count, see this
    // module's own header (audit Critical #6).
    let curve = byName.get(name);
    const isFirstRow = !curve;
    if (!curve) {
      curve = { name, type: "", points: [] };
      byName.set(name, curve);
      curves.push(curve);
    }
    // NaN fallback (not 0) so an absent token (the legal "Name Type only"
    // first row) is silently skipped below, while a present-but-garbled
    // token still warns.
    const x = parseNum(isFirstRow ? row[2] : row[1], NaN, `[CURVES] "${name}" X`, warnings);
    const y = parseNum(isFirstRow ? row[3] : row[2], NaN, `[CURVES] "${name}" Y`, warnings);
    if (isFirstRow) curve.type = row[1] ?? "";
    if (Number.isNaN(x) || Number.isNaN(y)) continue;
    curve.points.push({ x, y });
  }
  return curves;
}

export function writeCurves(
  raw: ReadonlyMap<string, string[][]>,
  curves: Curve[]
): Map<string, string[][]> {
  const rows: string[][] = [];
  for (const curve of curves) {
    curve.points.forEach((point, i) => {
      const row =
        i === 0
          ? [curve.name, curve.type, String(point.x), String(point.y)]
          : [curve.name, String(point.x), String(point.y)];
      rows.push(row);
    });
  }
  const next = new Map(raw);
  next.set("CURVES", rows);
  return next;
}

/** A curve name never collides with a node/link name in real SWMM — separate namespaces, so `existingNames` here is curve names only. */
export function nextAvailableCurveName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "C");
}
